import React, {useMemo} from "react"
import {School, SchoolClass, Subscription} from "models"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import ContactMySchool from "./contact-my-school"
import Devise from "@kaspernj/api-maker/build/devise"
import {digg} from "diggerize"
import envSense from "env-sense"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import {loading} from "components/layout/loading-blocker"
import memo from "set-state-compare/build/memo"
import OptionBox from "./option-box"
import Overlay from "components/layout/overlay"
import PageContentsPresentation from "components/page-contents/presentation"
import PlansSignUpModal from "components/plans/sign-up-modal"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import useAnythingPro from "components/subscriptions/use-anything-pro"
import useCurrentCountry from "components/utils/use-current-country"
import useCurrentUser from "@kaspernj/api-maker/build/use-current-user"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import usePath from "on-location-changed/build/use-path"
import useShowMenu from "components/chalky-layout/use-show-menu"
import useSideBarMenuOpened from "components/front-layout/use-side-bar-menu-opened"
import useTesting from "shared/use-testing"
import {View} from "react-native"

export default memo(shapeComponent(class ComponentsProUpgradeOverlay extends ShapeComponent {
  static defaultProps = {
    mainContentIdentifier: "pro/upgrade-overlay/main-content",
    school: null,
    schoolClass: null
  }

  static propTypes = propTypesExact({
    mainContentIdentifier: PropTypes.string,
    onRequestClose: PropTypes.func.isRequired,
    school: PropTypes.instanceOf(School),
    schoolClass: PropTypes.instanceOf(SchoolClass)
  })

  setup() {
    const {school: propSchool, schoolClass: propSchoolClass} = this.props
    const params = useParams()
    const path = usePath()
    const schoolClassIdFromPath = path?.match(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i)?.[0]
    let matchParams = params

    if (!params?.school_class_id && !params?.id && schoolClassIdFromPath) {
      matchParams = {school_class_id: schoolClassIdFromPath}
    }
    const {schoolClass: routeSchoolClass} = useModel(SchoolClass, {
      match: {params: matchParams},
      optional: true,
      preload: ["school.account", "school.country"],
      select: {
        Account: ["id"],
        Country: ["countryCode"],
        School: ["name"],
        SchoolClass: ["id", "schoolId"]
      }
    })
    const school = propSchool
    const schoolClass = propSchoolClass || routeSchoolClass
    const {locale, t} = useI18n({namespace: "js.components.pro.upgrade_overlay"})
    const {size, showMenu} = useShowMenu()
    const currentCountry = useCurrentCountry()
    const currentUser = useCurrentUser()
    const {expiredProTrial, hasProTrial} = useAnythingPro({currentUser})

    let haveOrHadTrial = false

    if (expiredProTrial || hasProTrial) {
      haveOrHadTrial = true
    }

    if (locale == "da") {
      this.requestQuoteURL = "https://planetwoof.io/da/kontakt"
    } else if (locale == "no") {
      this.requestQuoteURL = "https://planetwoof.io/no/kontakt"
    } else if (locale == "se") {
      this.requestQuoteURL = "https://planetwoof.io/sv/kontakta"
    } else {
      this.requestQuoteURL = "https://planetwoof.io/contact-us"
    }

    this.setInstance({
      haveOrHadTrial,
      menuSize: size,
      pageContentsLiquidVariables: useMemo(() => ({school, schoolClass}), [school, schoolClass]),
      school,
      schoolClass,
      showMenu,
      sideBarMenuOpened: useSideBarMenuOpened(),
      t,
      currentUser,
      currentCountry,
      locale
    })
    this.useStates({
      heights: {},
      mode: "initial",
      planResourceType: null,
      resource: null
    })
  }

  render() {
    const {school, schoolClass} = this.tt
    const {mode, planResourceType, resource} = this.s

    if (mode == "initial") {
      return this.initialContent()
    } else if (mode == "sign-up") {
      return (
        <PlansSignUpModal
          onBackPress={this.tt.onPlansSignUpModalBackPress}
          onRequestClose={this.tt.onPlansSignUpModalRequestClose}
          onSignUpComplete={this.tt.onSignUpComplete}
          planResourceType={planResourceType}
          resource={resource}
          school={school}
          schoolClass={schoolClass}
        />
      )
    } else if (mode == "contact-my-school") {
      return (
        <ContactMySchool
          onCloseWindowPressed={this.tt.onContactMySchoolCloseWindowPressed}
          onRequestClose={this.tt.onContactMySchoolRequestClose}
        />
      )
    }

    throw new Error(`Unhandled mode: ${mode}`)
  }

  initialContent() { // eslint-disable-line complexity
    const {haveOrHadTrial, menuSize, requestQuoteURL, school, schoolClass, showMenu, sideBarMenuOpened, t} = this.tt
    const {mainContentIdentifier, onRequestClose} = this.p
    const {heights} = this.s
    const testing = useTesting()
    const {isNative} = envSense()
    let left = -121

    if (sideBarMenuOpened) {
      left = 120
    } else if (showMenu) {
      left = menuSize
    }

    let minHeight = 0

    for (const identifier in heights) {
      const optionBoxHeight = heights[identifier]

      if (optionBoxHeight > minHeight) {
        minHeight = optionBoxHeight
      }
    }

    return (
      <Overlay
        color={isNative ? "dark" : "blur"}
        dataSet={this.overlayDataSet ||= {component: "user/pro/upgrade-overlay"}}
        onRequestClose={onRequestClose}
        zIndex={8}
      >
        <View style={this.cache("contentContainerStyle", {paddingLeft: left, alignItems: "center"}, [left])}>
          <PageContentsPresentation
            identifier={mainContentIdentifier}
            liquidVariables={this.cache("mainContentLiquidVariables", {school, schoolClass}, [school, schoolClass])}
          />
          <View style={this.upgradeOptionsViewStyle ||= {flexDirection: "row", alignItems: "center", marginTop: 50}}>
            <OptionBox
              aboveButtonContent={this.tt.stayBasicAboveButtonContent}
              buttonProps={this.optionBoxStayBasicButtonProps ||= {dataSet: {class: "stay-basic-button"}}}
              buttonText={t(".stay_basic")}
              first
              identifier="stay-basic"
              minHeight={minHeight}
              onButtonPress={this.tt.onStayBasicPressed}
              onLayout={this.tt.onBasicOptionBoxLayout}
            >
              <PageContentsPresentation
                identifier="pro/upgrade-overlay/stay-basic-content"
                liquidVariables={this.tt.pageContentsLiquidVariables}
              />
            </OptionBox>
            {!haveOrHadTrial &&
              <OptionBox
                aboveButtonContent={this.tt.startTrialAboveButtonContent}
                buttonProps={this.optionBoxStartTrialButtonProps ||= {dataSet: {class: "start-trial-button"}}}
                buttonText={t(".start_trial")}
                focus
                identifier="start-trial"
                minHeight={minHeight}
                onButtonPress={this.tt.onStartTrialPressed}
              >
                <PageContentsPresentation
                  identifier="pro/upgrade-overlay/start-trial-content"
                  liquidVariables={this.tt.pageContentsLiquidVariables}
                />
              </OptionBox>
            }
            {haveOrHadTrial &&
              <OptionBox
                aboveButtonContent={this.tt.proTeacherAboveButtonContent}
                buttonProps={this.optionBoxUpgradeNowButtonProps ||= {dataSet: {class: "pro-teacher-button"}}}
                buttonText={t(".choose")}
                focus
                identifier="pro-teacher"
                minHeight={minHeight}
                onButtonPress={this.tt.onProTeacherButtonPress}
              >
                <PageContentsPresentation
                  identifier="pro/upgrade-overlay/pro-teacher-content"
                  liquidVariables={this.tt.pageContentsLiquidVariables}
                />
              </OptionBox>
            }
            <OptionBox
              aboveButtonContent={this.tt.proClassAboveButtonContent}
              buttonProps={this.proClassOptionBoxDataButtonProps ||= {dataSet: {class: "pro-class-button"}}}
              buttonText={t(".request_quote")}
              href={testing ? undefined : requestQuoteURL}
              identifier="pro-class"
              minHeight={minHeight}
              onButtonPress={testing ? this.tt.onProClassButtonPress : undefined}
              onLayout={this.tt.onBasicOptionBoxLayout}
              schoolPlan
              target={testing ? undefined : "_blank"}
            >
              <PageContentsPresentation
                identifier="pro/upgrade-overlay/pro-class-content"
                liquidVariables={this.tt.pageContentsLiquidVariables}
              />
            </OptionBox>
            {schoolClass && testing &&
              <OptionBox
                aboveButtonContent={this.tt.proClassAboveButtonContent}
                buttonProps={this.proClassUpgradeOptionBoxDataButtonProps ||= {dataSet: {class: "pro-class-upgrade-button"}}}
                buttonText={t(".choose")}
                identifier="pro-class"
                minHeight={minHeight}
                onButtonPress={this.tt.onProClassUpgradeButtonPress}
                onLayout={this.tt.onBasicOptionBoxLayout}
                schoolPlan
              >
                <PageContentsPresentation
                  identifier="pro/upgrade-overlay/pro-class-content"
                  liquidVariables={this.tt.pageContentsLiquidVariables}
                />
              </OptionBox>
            }
            <OptionBox
              aboveButtonContent={this.tt.proSchoolAboveButtonContent}
              buttonProps={this.proSchoolOptionBoxDataButtonProps ||= {dataSet: {class: "pro-school-button"}}}
              buttonText={t(".request_quote")}
              href={testing ? undefined : requestQuoteURL}
              identifier="pro-school"
              last
              minHeight={minHeight}
              onButtonPress={testing ? this.tt.onProSchoolButtonPress : undefined}
              onLayout={this.tt.onBasicOptionBoxLayout}
              schoolPlan
              target={testing ? undefined : "_blank"}
            >
              <PageContentsPresentation
                identifier="pro/upgrade-overlay/pro-school-content"
                liquidVariables={this.tt.pageContentsLiquidVariables}
              />
            </OptionBox>
          </View>
        </View>
      </Overlay>
    )
  }

  proClassAboveButtonContent = () => <PageContentsPresentation
    identifier="pro/upgrade-overlay/pro-class-above-button-content"
    liquidVariables={this.tt.pageContentsLiquidVariables}
  />

  proSchoolAboveButtonContent = () => <PageContentsPresentation
    identifier="pro/upgrade-overlay/pro-school-above-button-content"
    liquidVariables={this.tt.pageContentsLiquidVariables}
  />

  proTeacherAboveButtonContent = () => <PageContentsPresentation
    identifier="pro/upgrade-overlay/pro-teacher-above-button-content"
    liquidVariables={this.tt.pageContentsLiquidVariables}
  />

  stayBasicAboveButtonContent = () => <PageContentsPresentation
    identifier="pro/upgrade-overlay/stay-basic-above-button-content"
    liquidVariables={this.tt.pageContentsLiquidVariables}
  />

  startTrialAboveButtonContent = () => <PageContentsPresentation
    identifier="pro/upgrade-overlay/start-trial-above-button-content"
    liquidVariables={this.tt.pageContentsLiquidVariables}
  />

  onConfirmUpgradeRequestClose = () => this.setState({mode: "initial"})
  onContactMySchoolCloseWindowPressed = () => this.setState({mode: "initial"})
  onContactMySchoolRequestClose = () => this.setState({mode: "initial"})
  onPlansSignUpModalBackPress = () => this.setState({mode: "initial"})
  onPlansSignUpModalRequestClose = () => {
    this.setState({mode: "initial"})
    this.p.onRequestClose()
  }

  onProClassButtonPress = () => this.setState({mode: "contact-my-school"})
  onProClassUpgradeButtonPress = () => this.setState({mode: "sign-up", planResourceType: "SchoolClass", resource: this.tt.schoolClass})
  onProSchoolButtonPress = () => this.setState({mode: "contact-my-school"})

  onBasicOptionBoxLayout = ({e, identifier}) => {
    const height = digg(e, "nativeEvent", "layout", "height")

    if (this.s.heights[identifier] !== height) {
      const newHeights = {...this.s.heights}

      newHeights[identifier] = height

      this.setState({heights: newHeights})
    }
  }

  onSignUpComplete = () => {
    // We request to close the entire thing, once the user has signed up to something.
    this.p.onRequestClose()
  }

  onStayBasicPressed = async () => {
    const requestCloseResult = this.p.onRequestClose({reason: "stay-basic"})

    if (requestCloseResult instanceof Promise) {
      await requestCloseResult
    }
  }

  onStartTrialPressed = async () => {
    try {
      await loading(this.t(".starting_trial"), async () => {
        await Subscription.startProUserTrial()
        this.pushTrialStartedDataLayerEvent({
          language: this.tt.locale,
          market: this.tt.currentCountry?.countryCode(),
          userId: this.tt.currentUser?.id()
        })
        FlashMessage.success(this.t(".your_pro_trial_has_started"))
        this.p.onRequestClose()
      })
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }

  onProTeacherButtonPress = () => this.setState({mode: "sign-up", planResourceType: "User", resource: Devise.currentUser()})

  pushTrialStartedDataLayerEvent = ({language, market, plan = "pro_teacher", userId}) => {
    const payload = {
      event: "trial_started",
      plan,
      user_id: userId
    }

    if (market) {
      payload.market = market
    }

    if (language) {
      payload.language = language
    }

    window.dataLayer ||= []
    window.dataLayer.push(payload)
  }
}))
